import React, { useEffect, useState } from "react";
import {
    Card, CardContent, Typography, CircularProgress, Button, List, ListItem, Grid, Box,
    Modal, FormControl, RadioGroup, FormControlLabel, Radio, FormLabel
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ComponentHeaderTitles from "../ComponentHeaderTitles";

import ProductDetailsButton from './ProductDetailsButton';
import ProductDetailsModal from './ProductDetailsModal';
import { formatNumberAsEuro } from "./helpers";

/**
 * Enum for the chosen product.
 * @readonly
 * @enum {string}
 */


const BACKEND_URL = "https://offerte.api.cleanenergy.nl/index_new";

/**
 * A component that allows the user to select a product from a list of options.
 *
 * @param {function} setChosenProduct - A function to update the chosen product.
 * @returns {JSX.Element} The ProductKiezer component.
 */
function PakketKiezer({ address, chosenProduct, chosenUsage, returnedEnergyAmount, chosenEntity, setIsFetchingData, setChosenPakket }) {
    const [fetchingData, setFetchingData] = useState(true);
    const [products, setProducts] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const [modalOpen, setModalOpen] = useState(false); // State to control modal visibility
    const [modalProduct, setModalProduct] = useState(null); // State to hold the product for the modal

    /**
     * Opens the modal and sets the product for which the details should be shown.
     * @param {*} product The product to display details for.
     */
    const handleOpenModal = (product) => {
        setModalProduct(product); // Set the product for the modal
        setModalOpen(true); // Open the modal
    };

    // Define a styled Typography component for the total price
    const TotalPrice = styled(Typography)(({theme}) => ({
        fontSize: '2em',
        fontWeight: 'bold'
    }));

    // Define a styled Box component for the price tags
    const PriceTag = styled(Box)(({theme}) => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1, 0),
        fontSize: '1.5em',
    }));
    const getPricingSummary = (product) => {
        let zakelijk = chosenEntity === "Zakelijk" ? true : false;
        const electricitySummary = zakelijk ? product.cost_specifications?.electricity?.total_with_price_cap : product.cost_specifications?.electricity?.total_with_price_cap_inc_vat;
        const gasSummary = zakelijk ? product.cost_specifications?.gas?.total_with_price_cap : product.cost_specifications?.gas?.total_with_price_cap_inc_vat;

        return {
            electricityTotal: electricitySummary,
            gasTotal: gasSummary
        };
    };

    const getTotalPrice = (product) => {
        let zakelijk = chosenEntity === "Zakelijk" ? true : false;
        const totalPrice = product.cost_specifications && (
            zakelijk
                ? product.cost_specifications.total.total
                : product.cost_specifications.total.total_inc_vat
        ) || 0;

        return totalPrice;
    }


    const getProductName = (product) => {
        return product.name;
    }


    /**
     * Closes the modal.
     */
    const handleCloseModal = () => {
        setModalOpen(false); // Close the modal
    };
    /**
     * Sets the chosen pakket in the parent component and updates the selected product.
     * @param {*} product
     */
    const handleCardClick = (product) => {
        setChosenPakket(product);
        setSelectedProductId(product.id);

    };

    /**
     * Get the product parameter based on the chosen product.
     * @returns {string} The product parameter. False if the chosen product is invalid.
     */
    const get_product_parameter = () => {
        if (chosenProduct.includes("Gas") && !chosenProduct.includes("Electriciteit")) {
            return "g";
        } else if (chosenProduct.includes("Electriciteit") && !chosenProduct.includes("Gas")) {
            return "e";
        } else if (chosenProduct.includes("Gas") && chosenProduct.includes("Electriciteit")) {
            return "e_g";
        } else {
            console.error("Invalid chosen product", chosenProduct);
        }
        return false;
    }
    const get_return_parameters = () => {
        console.log(returnedEnergyAmount);
        if (returnedEnergyAmount) {
            return {
                "return": 1,
                "return_e_high": returnedEnergyAmount.return_high,
                "return_e_low": returnedEnergyAmount.return_low,
                "has_return": 1,
            }
        } else {
            return {
                "return": 0,
                "return_e_high": null,
                "return_e_low": null,
                "has_return": 0,
            }
        }
    }



    /**
     * Get the parameters for the API call.
     * @returns {Object} The parameters for the API call. False if the chosen product is invalid.
     * See https://developer.salesdock.nl/#b28c47a9-2d30-4310-957b-22e9fdb8bff0 for parameter documentation.
     */
    const get_parameters = () => {
        const product_parameter = get_product_parameter();
        if (!product_parameter) {
            return false;
        }

        const return_parameters = get_return_parameters();
        const housenumberSuffix = address.housenumberSuffix === "Geen" ? null : address.housenumberSuffix;

        const parameters_without_return = {
            "dynamic": 1,
            "m": "products",
            "postcode": address.postcode,
            "housenumber": address.housenumber,
            "suffix": housenumberSuffix,
            "tarifftype": 1, // 1 = double meter
            "type": product_parameter,
            "usage_e_high": chosenUsage.e_high ? chosenUsage.e_high : null,
            "usage_e_low": chosenUsage.e_low ? chosenUsage.e_low : null,
            "usage_e_single": chosenUsage.e_single ? chosenUsage.e_single : null,
            "usage_g": chosenUsage.gas ? chosenUsage.gas : null,
            "business": chosenEntity === "Zakelijk" ? 1 : 0,
        }

        const parameters_with_return_parameters = {
            ...parameters_without_return,
            ...return_parameters
        }

        return parameters_with_return_parameters;
    }

    /**
     * Ensures that the first package is selected after the packages are fetched.
     * @param {*} data
     */
    function preselect_first_product(data) {
        setChosenPakket(data[0]);
    };

    function validateApiResponse(data) {
        if (!data || !(typeof data === 'object')) {
            console.error('Error fetching products: invalid response', data);
            return false;
        }
        // check if data[0] has name property
        if (!data[0].hasOwnProperty("name")) {
            console.error('Error fetching products: invalid response', data);
            return false;
        }
        return true;
    }

    async function get_data_from_api() {
        const url = new URL(BACKEND_URL);
        const params = get_parameters();
        if (!params) {
            return false;
        }
        url.search = new URLSearchParams(params).toString();
        setFetchingData(true);
        setIsFetchingData(true);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        })
        try {
            if (response.ok) {
                const data = await response.json();
                // log type of data
                if (!validateApiResponse(data)) {
                    console.error('Error fetching products: invalid response', response);
                    setFetchingData(false);
                    setIsFetchingData(false);
                    return false;
                }
                setProducts(data);
                preselect_first_product(data);
                setFetchingData(false);
                setIsFetchingData(false);
                return data;
            } else {
                setProducts([]);
                console.error('Error fetching products', response);
                setFetchingData(false);
                setIsFetchingData(false);
                return false;
            }
        } catch (error) {
            console.error('Error fetching products', error);
            setFetchingData(false);
            setIsFetchingData(false);
            return false;
        }
    }

    // When the component mounts, we call the API to get the products
    useEffect(() => {
        get_data_from_api();
    }, [chosenUsage, chosenEntity]);

    useEffect(() => {
        // Set the first package as the chosen one after packages are fetched
        if (products.length > 0 && !selectedProductId) {
            const firstProduct = products[0];
            handleCardClick(firstProduct);
            setSelectedProductId(firstProduct.id); // Set the selected product ID
        }
    }, [products]);

    /**
     * TODO: Show a friendlier message when no products are found.
     */
    if (products.length === 0 && !fetchingData) {
        return <Typography>Helaas zijn er geen pakketten gevonden.</Typography>;
    }

    /**
     * Sets the expandedId when the user clicks on the "Bekijk tarieven" button.
     * @param {*} productId
     */
    const handleExpandClick = (productId) => {
        setExpandedId(expandedId === productId ? null : productId);
    };



    /**
 * Renders the modal with pricing details.
 * @returns JSX.Element
 */
    // const renderModal = () => {
    //     if (!modalProduct) return null; // Do not render the modal if there is no product
    //
    //     const { electricityTotal, gasTotal } = getPricingSummary(modalProduct);
    //     const electricityTotalParsed = parseEuropeanNumber(electricityTotal);
    //     const gasTotalParsed = parseEuropeanNumber(gasTotal);
    //
    //
    //     return (
    //         <Modal
    //             open={modalOpen}
    //             onClose={handleCloseModal}
    //             aria-labelledby="pricing-details-title"
    //             aria-describedby="pricing-details-description"
    //         >
    //             <div class="pricing-details-modal">
    //                 <Typography id="pricing-details-title" variant="h6" component="h2">
    //                     Prijsopbouw verwachte kosten
    //                 </Typography>
    //                 <div id="pricing-details-description">
    //                     {renderPricingDetails(modalProduct, electricityTotalParsed, gasTotalParsed)}
    //                 </div>
    //                 <Button onClick={handleCloseModal}>Sluiten</Button>
    //             </div>
    //         </Modal>
    //     );
    // };



    /**
     * We show a spinner when the products are being fetched.
     */
    // if (products.length === 0 && fetchingData) {
    //     return (
    //         <div className="spinner-container">
    //             <CircularProgress className='data-fetch-spinner' />
    //         </div>
    //     );
    // }
    const productEntries = Object.values(products); // Convert object to array so we can map over it
    const StyledPriceDetails = styled(Grid)(({ theme }) => ({
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1, 0),
        '&:last-child': {
            borderBottom: 'none',
        },
    }));

    const parseEuropeanNumber = (numberString) => {
        if (!numberString) {
            return null;
        }
        const noThousandsSeparator = numberString.replace(/\./g, '');
        const standardDecimalSeparator = noThousandsSeparator.replace(/,/, '.');
        return parseFloat(standardDecimalSeparator);
    };



    return (
        <div>
            <ComponentHeaderTitles title="Onze aanbieding"/>

            <FormControl component="fieldset">
                <RadioGroup name="product" value={selectedProductId || products[0]?.id}>
                    {productEntries.map((product) => {
                        const { electricityTotal, gasTotal } = getPricingSummary(product);
                        const electricityTotalParsed = parseEuropeanNumber(electricityTotal);
                        const gasTotalParsed = parseEuropeanNumber(gasTotal);
                        const totalPrice = parseEuropeanNumber(getTotalPrice(product));

                        console.log("show me the product", product);

                        return (
                            <Card
                                key={product.id}
                                variant="outlined"
                                className={selectedProductId === product.id ? "selected" : ""}
                                onClick={() => handleCardClick(product)}
                            >
                                <CardContent>
                                    <div class="aanbod-header">
                                        {getProductName(product)}
                                    </div>

                                    <StyledPriceDetails container>
                                        <Grid item xs={6}>
                                            <TotalPrice>
                                                <div className="voorschot-per-maand-label">
                                                    Voorschot per maand
                                                </div>
                                            </TotalPrice>
                                        </Grid>
                                        <Grid item xs={6} style={{ textAlign: 'right' }}>
                                            <TotalPrice>
                                                <div className="voorschot-per-maand-prijs">
                                                     {/*{formatNumberAsEuro((parseFloat(electricityTotalParsed || 0) + parseFloat(gasTotalParsed || 0)) / 12) }*/}
                                                    {formatNumberAsEuro(( parseFloat(totalPrice) / 12) ) }
                                                </div>
                                            </TotalPrice>
                                        </Grid>
                                    </StyledPriceDetails>

                                    {/*<StyledPriceDetails container>*/}
                                    {/*    <Grid item xs={6}>*/}
                                    {/*        <Typography variant="body2" color="textSecondary">*/}
                                    {/*            Verwachte kosten per jaar*/}
                                    {/*        </Typography>*/}
                                    {/*    </Grid>*/}
                                    {/*    <Grid item xs={6} style={{ textAlign: 'right' }}>*/}
                                    {/*        <Typography variant="body2" color="textSecondary">*/}
                                    {/*            { formatNumberAsEuro(*/}

                                    {/*                parseFloat(electricityTotalParsed || 0) + parseFloat(gasTotalParsed || 0)*/}

                                    {/*            ) }*/}

                                    {/*        </Typography>*/}
                                    {/*    </Grid>*/}
                                    {/*</StyledPriceDetails>*/}
                                    <Grid container justifyContent="flex-end">
                                        <Grid item>
                                            <ProductDetailsButton onClick={() => handleOpenModal(product)} />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                                <ProductDetailsModal
                                    modalOpen={modalOpen}
                                    handleCloseModal={handleCloseModal}
                                    modalProduct={product}
                                    chosenEntity={chosenEntity}
                                    electricityTotalParsed = {electricityTotalParsed}
                                    gasTotalParsed = {gasTotalParsed}
                                />
                            </Card>

                        );
                    })}
                </RadioGroup>
                {fetchingData && (
                <div className="spinner-container">
                <CircularProgress className='data-fetch-spinner' />
                </div>
            )}

            </FormControl>
            {/* Render the modal outside of the map function */}

        </div>
    );
}
PakketKiezer.displayName = "PakketKiezer";
export default PakketKiezer;
